import { debounce } from 'lodash';
import type { DebouncedFunc } from 'lodash';
import * as Sentry from '@sentry/react';
import { IndexDbTable, IndexDbTableConfig } from './IndexDbTable';
import type { UserActionEvent } from './types';
import { addInteractionEvents } from '../mutations/addInteractionEvents';

const DEFAULT_FLUSH_INTERVAL_MS = 2_000;
const MAX_RETRY_COUNT = 10;
const BATCH_SIZE = 100;

/**
 * IndexedDB store for user action events.
 *
 * Specialises IndexDbTable for the UserActionEvent schema.
 *
 * ## Flush pipeline
 * - On init: flushes any records left over from the previous session (on-load
 *   recovery).
 * - On each write: schedules a debounced flush (default 2 s, configurable via
 *   `setFlushInterval`).
 * - On page unload: fires `flushBeforeUnload()` via the registered beforeunload
 *   handler so the browser completes the HTTP request after page teardown.
 *
 * ## Retry tracking
 * When the BFF returns a non-Success status (Retryable, Failed, etc.),
 * `retryCount` is incremented and the record is written back via `put()`. At
 * MAX_RETRY_COUNT the event is reported to Sentry and dropped from the store.
 *
 * Schema:
 *   - Database: cc-user-action-events
 *   - Object store: events
 *   - Key path: eventId (ULID / UUIDv7)
 */
export class UserActionEventTable extends IndexDbTable<UserActionEvent> {
  readonly tableId = 'userActionEvents';

  protected readonly config: IndexDbTableConfig = {
    dbName: 'cc-user-action-events',
    storeName: 'events',
    keyPath: 'eventId',
  };

  private isFlushInProgress = false;
  private isKeepaliveInProgress = false;
  private scheduleFlush: DebouncedFunc<() => void>;
  private readonly beforeUnloadHandler: () => void;

  constructor() {
    super();
    this.scheduleFlush = this.makeDebounced(DEFAULT_FLUSH_INTERVAL_MS);

    // On-load recovery: flush any records that were not delivered before the
    // previous session ended.
    this.on('initialized', () => void this.flush());

    // Post-write flush: schedule a batched delivery shortly after each write.
    this.on('recordAdded', () => this.scheduleFlush());

    // Page teardown: flush with keepalive so the browser completes the request
    // after the page starts unloading.
    this.beforeUnloadHandler = () => void this.flushBeforeUnload();
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  /**
   * Updates the debounce delay used by the post-write flush.
   * Flushes any pending debounced call before replacing the debouncer so that
   * records queued since the last write are not silently dropped.
   */
  setFlushInterval(ms: number): void {
    this.scheduleFlush.flush();
    this.scheduleFlush = this.makeDebounced(ms);
  }

  /**
   * Reads up to BATCH_SIZE records from IndexedDB, posts them as a batch to
   * the BFF, and handles each result:
   *
   * - Success → deleted from the store.
   * - Non-Success (Retryable, Failed, etc.) → retryCount incremented and
   *   written back via put(); at MAX_RETRY_COUNT the event is reported to
   *   Sentry and dropped.
   *
   * A follow-up flush is scheduled when the batch did not cover all records
   * (hasMore) or when there are retryable events that were not dropped
   * (toRetry.length > toDrop.length — avoids a spurious flush when all
   * retryable events have hit MAX_RETRY_COUNT and been dropped).
   *
   * On network/unexpected errors, scheduleFlush() re-arms the pipeline for
   * stranded records. Keepalive flushes do not schedule a follow-up because
   * they are fire-and-forget; only the regular pipeline retries on error.
   *
   * ## Concurrency model (two independent flags)
   *
   * isFlushInProgress | isKeepaliveInProgress | regular flush | page-unload flush
   * ----------------- | --------------------- | ------------- | -----------------
   * false             | false                 | proceeds      | proceeds
   * true              | false                 | skipped       | proceeds
   * false             | true                  | skipped       | skipped
   * true              | true                  | skipped       | skipped
   *
   * Each flush sets its own flag on entry and clears only that flag in finally,
   * so neither invocation can prematurely unblock the other.
   *
   * The page-unload flush is a last-ditch delivery that must not wait for an
   * in-flight regular flush, because the browser may abort that fetch before
   * it completes. The BFF is idempotent, so concurrent submission of the same
   * batch is safe.
   *
   * Trade-off: if both flushes receive Retryable for the same event, each reads
   * the same stale retryCount and writes staleCount + 1 — the second put()
   * overwrites the first. Net result: retryCount advances by 1 instead of 2 per
   * concurrent pair. This delays the MAX_RETRY_COUNT drop by at most one extra
   * flush cycle per overlap; it does not cause data corruption.
   */
  async flush(options?: { onPageUnload?: boolean }): Promise<void> {
    if (!this.isAvailable) return;
    const isKeepalive = !!options?.onPageUnload;

    if (isKeepalive) {
      if (this.isKeepaliveInProgress) return;
      this.isKeepaliveInProgress = true;
    } else {
      if (this.isFlushInProgress || this.isKeepaliveInProgress) return;
      this.isFlushInProgress = true;
    }

    try {
      const all = await this.getAll();
      if (all.length === 0) return;

      const events = all.slice(0, BATCH_SIZE);
      const hasMore = all.length > BATCH_SIZE;

      const { toDelete, toRetry } = await addInteractionEvents(events, {
        keepalive: isKeepalive,
      });
      const { toDrop } = await this.processRetries(events, toRetry);

      await this.deleteMany([...toDelete, ...toDrop]);

      if (hasMore || toRetry.length > toDrop.length) {
        this.scheduleFlush();
      }
    } catch {
      if (!isKeepalive && this.isAvailable) this.scheduleFlush();
    } finally {
      if (isKeepalive) {
        this.isKeepaliveInProgress = false;
      } else {
        this.isFlushInProgress = false;
      }
    }
  }

  private async processRetries(
    events: UserActionEvent[],
    toRetry: string[],
  ): Promise<{ toDrop: string[] }> {
    if (toRetry.length === 0) return { toDrop: [] };

    const retrySet = new Set(toRetry);
    const toDrop: string[] = [];
    const puts: Promise<void>[] = [];

    for (const event of events) {
      if (!retrySet.has(event.eventId)) continue;

      const currentRetryCount =
        typeof event.retryCount === 'number' &&
        Number.isFinite(event.retryCount)
          ? event.retryCount
          : 0;
      const updated = { ...event, retryCount: currentRetryCount + 1 };

      if (updated.retryCount >= MAX_RETRY_COUNT) {
        Sentry.captureMessage(
          'UserActionEvent exceeded max retries and was dropped',
          {
            level: 'warning',
            extra: { eventId: event.eventId, event: event.event },
          },
        );
        toDrop.push(event.eventId);
      } else {
        puts.push(this.put(updated));
      }
    }

    await Promise.all(puts);
    return { toDrop };
  }

  /** Cancels pending flushes, removes the beforeunload listener, and closes the IDB connection. */
  destroy(): void {
    this.scheduleFlush.cancel();
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    this.dispose();
  }

  /**
   * Variant of `flush` for use in beforeunload handlers.
   *
   * Passes `onPageUnload: true` so the fetch is sent with keepalive, allowing
   * the browser to complete the HTTP request even after page teardown begins.
   */
  private async flushBeforeUnload(): Promise<void> {
    try {
      await this.flush({ onPageUnload: true });
    } catch {
      // Fire-and-forget from beforeunload; errors are not actionable.
    }
  }

  private makeDebounced(ms: number): DebouncedFunc<() => void> {
    return debounce(() => void this.flush(), ms);
  }
}
